import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { CoreUtils } from '@nhl/nhl-breakaway-ui';
import Headshot, { HEADSHOT_TYPES } from '@nhl/nhl-react-ui/lib/Headshot';

import CommonMessages from '~/core/commonMessages';
import { PlayerPosition } from '~/core/enums';
import { getPlayerHref, parseValueFromLocalizedAttribute } from '~/core/utils';

import { GameCenterTeam, Player } from '../../types';

import {
    FirstName,
    LastName,
    HeadshotContainer,
    PlayerContainer,
    SIZES,
    SweaterAndPosition,
    PlayerNameContainer,
} from './styles';

type PlayerInfoProps = {
    isRightAligned?: boolean;
    isWebview: boolean;
    leader: Player;
    team?: GameCenterTeam;
};

export const PlayerInfo: FC<PlayerInfoProps> = ({ isRightAligned = false, isWebview, leader }) => (
    <PlayerContainer $isRightAligned={isRightAligned} href={getPlayerHref(leader.playerId, isWebview)} rel="noreferrer">
        <PlayerNameContainer $isRightAligned={isRightAligned}>
            <FirstName>{parseValueFromLocalizedAttribute(leader.firstName)}</FirstName>
            <LastName>{parseValueFromLocalizedAttribute(leader.lastName)}</LastName>
        </PlayerNameContainer>
        <SweaterAndPosition>
            <FormattedMessage
                description="Text that shows a players sweater number and position code seperated by a line divider Ex. #36 | C"
                defaultMessage="#{sweaterNumber} <divider></divider> <position></position>"
                values={{
                    divider: () => '•',
                    position: () => (
                        <CoreUtils.SafeFormattedMessage
                            message={CommonMessages.playerInfoDescriptions.abbreviations[leader.positionCode]}
                        />
                    ),
                    sweaterNumber: leader.sweaterNumber ?? (
                        <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
                    ),
                }}
            />
        </SweaterAndPosition>
    </PlayerContainer>
);

type PlayerHeadshotProps = {
    isRightAligned?: boolean;
    leader: Player;
    isWebview: boolean;
    backgroundColor?: string;
};

export const PlayerHeadshot: FC<PlayerHeadshotProps> = ({
    isRightAligned = false,
    leader,
    isWebview,
    backgroundColor,
}) => {
    const theme = useTheme();
    return (
        <HeadshotContainer
            $isRightAligned={isRightAligned}
            href={getPlayerHref(leader.playerId, isWebview)}
            rel="noreferrer"
        >
            <Headshot
                urlOverride={leader.headshot}
                type={leader.positionCode === PlayerPosition.goalie ? HEADSHOT_TYPES.GOALIE : HEADSHOT_TYPES.SKATER}
                size={SIZES.player.large}
                backgroundColor={backgroundColor || theme.Surface.secondary}
            />
        </HeadshotContainer>
    );
};
